import {Cell, Row, Worksheet} from 'exceljs';
import {dataTypeString} from 'src/components/data-types/utils/dataTypeString';
import {ColumnTypes} from 'src/modules/attribute/constants';
import {
    decorateCell,
    decorateLinkCell,
    decorateSheetTitleCell,
    decorateTableCell,
    decorateTableHeaderCell,
    setCellValue,
} from 'src/modules/downloads/cells/cells';
import {DEFAULT_DATE_FORMAT, HorizontalAlignment} from 'src/modules/downloads/cells/styles';
import {printLogo} from 'src/modules/downloads/commons/print-logo';
import {printMetadata} from 'src/modules/downloads/commons/print-metadata';
import {setColumnWidth} from 'src/modules/downloads/utils/set-column-width';
import {AttributeDetailInfo} from 'src/openapi/models/AttributeDetailInfo';
import {capitalize} from 'src/utils/capitalize';
import {getPossibleValuesOrDefault} from 'src/utils/getPossibleValuesOrDefault';

import {formatDate} from '@pb/kit-formatters';

const FIRST_ROW = 1;
const FIRST_ROW_HEIGHT = 30;
const TABLE_HEADER_HEIGHT = 35;
const TABLE_HEADER_ROW = 9;
const FIRST_DATA_ROW = 10;
const SHEET_TITLE = 'Attribute';
const LINK = 'Link:';
const ATTRIBUTE = 'Attribute:';
const LINK_CAPTION = 'View Attribute';
const NO_ATTRIBUTE_USAGE = 'No Usage';

export const printSheetHeader = (worksheet: Worksheet, userName: string, attributeName: string) => {
    const row = worksheet.getRow(FIRST_ROW) as Partial<Row>;
    row.height = FIRST_ROW_HEIGHT;

    const link = (cell: Partial<Cell>) =>
        cell.value = {text: LINK_CAPTION, hyperlink: window.location.href};

    const styleAttributeCaptionCell = (cell: Partial<Cell>) => {
        cell.font = {bold: true};
        return cell;
    };

    printLogo(worksheet);
    printMetadata(worksheet, userName);
    decorateSheetTitleCell(worksheet.getCell('D1'), setCellValue(SHEET_TITLE, HorizontalAlignment.RIGHT));
    decorateCell(worksheet.getCell('A3'), setCellValue(LINK, HorizontalAlignment.RIGHT));
    decorateLinkCell(worksheet.getCell('B3'), link);
    decorateCell(worksheet.getCell('A4'), setCellValue(ATTRIBUTE, HorizontalAlignment.RIGHT));
    decorateCell(worksheet.getCell('B4'), setCellValue(attributeName, HorizontalAlignment.LEFT));
    decorateCell(worksheet.getCell('B4'), styleAttributeCaptionCell);
};

export const printTableDataHeader = (worksheet: Worksheet) => {
    const row = worksheet.getRow(TABLE_HEADER_ROW) as Partial<Row>;
    row.height = TABLE_HEADER_HEIGHT;

    decorateTableHeaderCell(worksheet.getCell('A9'), 25,
        setCellValue(ColumnTypes.EVENT_CODE, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('B9'), 20,
        setCellValue(ColumnTypes.EVENT_TYPE, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('C9'), 20,
        setCellValue(ColumnTypes.EVENT_SOURCE, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('D9'), 70,
        setCellValue(ColumnTypes.ATTRIBUTE_DESCRIPTION, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('E9'), 20,
        setCellValue(ColumnTypes.DATA_TYPE, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('F9'), 25,
        setCellValue(ColumnTypes.POSSIBLE_VALUES, HorizontalAlignment.CENTER));
    setColumnWidth(worksheet.getCell('F9'), 50);
    decorateTableHeaderCell(worksheet.getCell('G9'), 15,
        setCellValue(ColumnTypes.ATTRIBUTE_DEPRECATED, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('H9'), 20,
        setCellValue(ColumnTypes.ATTRIBUTE_LAST_USAGE_DATE, HorizontalAlignment.CENTER));
};

export const printTableData = (worksheet: Worksheet, attributes: Array<AttributeDetailInfo>) => {
    attributes.forEach((attribute, index) => {
        const rowIndex = FIRST_DATA_ROW + index;
        const row = worksheet.getRow(rowIndex);
        const deprecated = capitalize(attribute.deprecated.toString());
        const lastUsageDate = attribute.lastUsageDate ?
                formatDate(attribute.lastUsageDate, DEFAULT_DATE_FORMAT)
            : NO_ATTRIBUTE_USAGE;

        decorateTableCell(row, 1, setCellValue(attribute.eventCode));
        decorateTableCell(row, 2, setCellValue(attribute.eventType));
        decorateTableCell(row, 3, setCellValue(attribute.source));
        decorateTableCell(row, 4, setCellValue(attribute.description ?? ''));
        decorateTableCell(row, 5, setCellValue(dataTypeString(attribute.fieldType, attribute.itemsType)));
        decorateTableCell(row, 6, setCellValue(getPossibleValuesOrDefault(attribute.possibleValues)));
        decorateTableCell(row, 7, setCellValue(deprecated, HorizontalAlignment.CENTER));
        decorateTableCell(row, 8, setCellValue(lastUsageDate, HorizontalAlignment.CENTER));
    });
};
